import { Grid } from '@mui/material';
import { Form, Formik } from 'formik';
import { transformResponseSaveDataAnpr } from 'shell/utils/save-data-anpr-response-transformer';
import { SaveDataAnpr } from 'shell/view-models/save-data-anpr';
import { Fragment, useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import SearchMessages, { MessageType } from 'shell/components/shared/search-messages';
import { __PERSON_API_URL__, __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData, ResponseAnprSaveData } from 'shell/models/person';
import { ItemVM } from 'shell/view-models/item';
import SubscriptionBirth from 'subscription/components/birth-cohabitation';
import { SubscriptionBirthCohabitationCrumbs } from 'subscription/constants/breadcrumb';
import { transformerSubscriptionBirthData } from 'subscription/utils/transformer-anpr';
import { SubscriptionBirthFamilyFormVM } from 'subscription/view-models/subscription-registration-birth-family';
import validationSchemaBirthCohabitation from './validation-schema';
import PmiTitle from 'shell/components/shared/title';
import SubscriptionButtons from 'subscription/components/shared/buttons';
import { DateTime } from 'luxon';
import { ActType } from 'shell/constants/act-type';
import { emptyDigital, emptyPaper } from 'shell/components/shared/act/constants';
import { transformerRequestSubscriptionBirthFamilyData } from 'subscription/utils/transformer-request-birth-family';

interface SubscriptionBirthCohabitationPageProps {
  isWithoutBreadcrumbs?: boolean;
  disabledFromAct?: boolean;
  goBackUrl?: string;
  initialValues?: SubscriptionBirthFamilyFormVM;
  onConfirm?: () => void;
  onHandleExit?: () => void;
}

const SubscriptionBirthCohabitationPage = ({
  isWithoutBreadcrumbs,
  disabledFromAct,
  goBackUrl = '/iscrizioni',
  initialValues = {},
  onConfirm,
  onHandleExit,
}: SubscriptionBirthCohabitationPageProps): React.ReactElement => {
  const navigate = useNavigate();

  const [queryParams] = useSearchParams();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const anprId = queryParams.get('anprId') || '';

  const [anpr] = useHttpFetch<HttpResponse<ResponseAnprData>, SubscriptionBirthFamilyFormVM>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            identificativoANPR: anprId,
            tipoScheda: '2',
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
          },
        },
      },
      transformer: (response) => {
        const transformedData = transformerSubscriptionBirthData(response, true);
        return {
          ...transformedData,
          editDataControl: {
            ...transformedData.editDataControl,
            dateValidity: DateTime.now(),
            requested: '',
            assume: '',
            subscribeTo: '',
            flag: '',
          },
          citizenship: {
            ...transformedData.citizenship,
            validity: null,
            state: null,
          },
          birthdayDocument: {
            actType: ActType.AttoCartaceo,
            paper: emptyPaper,
            digital: emptyDigital,
          },
        };
      },
    }
  );

  const [responseRegisterBirth, registerBirth] = useHttpFetch<
    HttpResponse<ResponseAnprSaveData>,
    SaveDataAnpr
  >(`${__PERSON_API_URL__}/persona/iscrizione-nascita`, {
    options: {
      method: 'POST',
    },
    transformer: transformResponseSaveDataAnpr,
  });

  const castedSavedData = responseRegisterBirth.data as SaveDataAnpr;

  // visibility
  const [visibility, setVisibility] = useState<{ error?: boolean; warning?: boolean }>({
    error: undefined,
    warning: undefined,
  });

  // on change visibility
  const onVisibility = useCallback(
    (open, type) => {
      const visibilityMessage = type === 'error' && !open ? false : open;

      setVisibility((oldState) => ({ ...oldState, [type]: visibilityMessage }));
    },
    [setVisibility]
  );

  const handleSubmit = useCallback(
    (values: SubscriptionBirthFamilyFormVM) => {
      const data = transformerRequestSubscriptionBirthFamilyData(values, true);
      (
        registerBirth({
          data,
        }) as ResponsePromise<HttpResponse<ResponseAnprSaveData>, SaveDataAnpr>
      ).then((response) => {
        const castedResponse = response as SaveDataAnpr;
        setVisibility({ error: undefined, warning: undefined });

        if (castedResponse?.errors?.length) {
          window.scrollTo(0, 0);
          return;
        }

        if (onConfirm) {
          onConfirm();
          return;
        }

        toast.success('Operazione eseguita con successo');
        navigate(goBackUrl);
      });
    },
    [navigate, registerBirth, setVisibility]
  );

  const handleExit = useCallback(() => {
    if (onHandleExit) {
      onHandleExit();
      return;
    }

    navigate(goBackUrl);
  }, [navigate]);

  //Errors
  const errorMessage = useMemo(() => {
    const errorToVisualize: { visibility?: boolean; title?: string; messages?: Array<ItemVM> } = {
      visibility: false,
      title: '',
    };

    if (castedSavedData?.errors?.length && visibility.error === undefined) {
      errorToVisualize.title =
        castedSavedData?.errors?.length > 1
          ? `Ci sono ${castedSavedData?.errors?.length} errori`
          : `C'è 1 errore`;
      errorToVisualize.messages = castedSavedData?.errors;
      errorToVisualize.visibility = true;
      return errorToVisualize;
    }

    errorToVisualize.visibility = visibility.error;

    return errorToVisualize;
  }, [visibility.error, castedSavedData]);

  return (
    <Fragment>
      {isWithoutBreadcrumbs ? undefined : (
        <Breadcrumbs items={SubscriptionBirthCohabitationCrumbs} />
      )}
      <div>
        <PmiTitle title="Iscrizione Nascita Convivenza" />
      </div>
      <SearchMessages
        type={MessageType.Error}
        title={errorMessage.title || ''}
        messages={errorMessage.messages || []}
        visibility={errorMessage.visibility || false}
        onChangeVisibility={onVisibility}
      />
      <SearchMessages
        type={MessageType.Warning}
        title={`Ci sono ${castedSavedData?.warnings?.length || ''} warning`}
        messages={castedSavedData?.warnings || []}
        visibility={
          visibility.warning !== undefined
            ? visibility.warning
            : (castedSavedData?.warnings || [])?.length > 0
        }
        onChangeVisibility={onVisibility}
      />
      {anpr.data && !anpr.isLoading && !anpr.hasErrors ? (
        <Formik
          initialValues={{
            ...((anpr.data || {}) as SubscriptionBirthFamilyFormVM),
            ...initialValues,
            familyCoexistence: {
              ...((anpr.data as SubscriptionBirthFamilyFormVM) || {}).familyCoexistence,
              membershipDate:
                (initialValues || {}).familyCoexistence?.membershipDate ||
                ((anpr.data as SubscriptionBirthFamilyFormVM) || {}).familyCoexistence
                  ?.membershipDate ||
                null,
              bondDate:
                (initialValues || {}).familyCoexistence?.bondDate ||
                ((anpr.data as SubscriptionBirthFamilyFormVM) || {}).familyCoexistence?.bondDate ||
                null,
            },
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchemaBirthCohabitation}
        >
          <Form>
            <Grid container spacing={2}>
              <SubscriptionButtons handleExit={handleExit} />
              <Grid item xs={12} md={10}>
                <SubscriptionBirth isCohabitation disabledFromAct={disabledFromAct} />
              </Grid>
            </Grid>
          </Form>
        </Formik>
      ) : undefined}
    </Fragment>
  );
};

SubscriptionBirthCohabitationPage.displayName = 'Subscription Birth page component';

export default SubscriptionBirthCohabitationPage;
